export const PERFORMANCE_TIMING_EVENT = "sprout:performance-timing";

export type PerformancePhaseName =
  | "auth.listener.first_callback"
  | "auth.id_token"
  | "auth.backend_login"
  | "auth.bootstrap.roles"
  | "auth.bootstrap.organization"
  | "auth.bootstrap.user_info"
  | "auth.bootstrap.abac_permissions"
  | "auth.bootstrap.role_policies"
  | "auth.bootstrap.subscription"
  | "auth.bootstrap.subscription_background"
  | "auth.bootstrap.aggregate"
  | "auth.ready"
  | "auth.protected_content"
  | "task.core.initial"
  | "task.core.warm_cache"
  | "task.core.refresh"
  | "task.supporting";

export interface PerformanceTimingDetail {
  name: PerformancePhaseName;
  durationMs: number;
  success: boolean;
}

export interface PerformanceTimingSummary {
  name: PerformancePhaseName;
  count: number;
  failures: number;
  p50Ms: number;
  p95Ms: number;
}

const MAX_MEMORY_SAMPLES = 500;
const samples: PerformanceTimingDetail[] = [];

export const performanceNow = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

export const recordPerformanceTiming = (
  name: PerformancePhaseName,
  startedAt: number,
  success = true,
): PerformanceTimingDetail => {
  const detail = { name, durationMs: Math.max(0, performanceNow() - startedAt), success };
  samples.push(detail);
  if (samples.length > MAX_MEMORY_SAMPLES) samples.splice(0, samples.length - MAX_MEMORY_SAMPLES);
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(PERFORMANCE_TIMING_EVENT, { detail }));
  }
  return detail;
};

export const getPerformanceTimingSamples = (): PerformanceTimingDetail[] =>
  samples.map((sample) => ({ ...sample }));

const percentile = (values: number[], fraction: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  return sorted[Math.max(0, Math.ceil(sorted.length * fraction) - 1)];
};

export const summarizePerformanceTimings = (
  input: PerformanceTimingDetail[] = samples,
): PerformanceTimingSummary[] => {
  const byName = new Map<PerformancePhaseName, PerformanceTimingDetail[]>();
  input.forEach((sample) => byName.set(sample.name, [...(byName.get(sample.name) || []), sample]));
  return Array.from(byName.entries()).map(([name, group]) => ({
    name,
    count: group.length,
    failures: group.filter((sample) => !sample.success).length,
    p50Ms: percentile(group.map((sample) => sample.durationMs), 0.5),
    p95Ms: percentile(group.map((sample) => sample.durationMs), 0.95),
  }));
};

export const clearPerformanceTimings = (): void => { samples.splice(0, samples.length); };

export const measureAsync = async <T>(
  name: PerformancePhaseName,
  work: () => Promise<T>,
): Promise<T> => {
  const startedAt = performanceNow();
  try {
    const result = await work();
    recordPerformanceTiming(name, startedAt, true);
    return result;
  } catch (error) {
    recordPerformanceTiming(name, startedAt, false);
    throw error;
  }
};
